import React, { useState, useEffect, useRef, useCallback } from 'react';
import DataTable from './DataTable';
import { getRetryRequests, approveRetryRequest, rejectRetryRequest, bulkApproveRetryRequests, getRetryEmailPreview } from '../../lib/adminApi';
import { formatToIST } from '../../lib/dateUtils';

function AdminRetryRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [statusFilter, setStatusFilter] = useState('PENDING'); // Default to PENDING
  const [rejectionReason, setRejectionReason] = useState('');
  const [emailPreview, setEmailPreview] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);
  const debounceRef = useRef(null);

  const fetchRequests = async (page = 1, status = statusFilter) => {
    setLoading(true);
    try {
      const data = await getRetryRequests(page, pagination.limit, status);
      setRequests(data.requests || []);
      setPagination({ page: data.page || 1, limit: data.limit || 20, total: data.total || 0 });
    } catch (err) {
      console.error('Failed to fetch retry requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchRequests(1, statusFilter);
  }, []);

  // Fetch email preview when confirmAction changes or rejection reason updates
  const fetchEmailPreview = useCallback(async (type, userEmail, reason = '') => {
    setLoadingPreview(true);
    try {
      const data = await getRetryEmailPreview(type, userEmail, reason);
      if (data.success) {
        setEmailPreview(data.html);
      }
    } catch (err) {
      console.error('Failed to fetch email preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  // Load preview when confirm modal opens
  useEffect(() => {
    if (confirmAction) {
      const type = confirmAction.type;
      const userEmail = confirmAction.row.userEmail;
      fetchEmailPreview(type, userEmail, type === 'reject' ? rejectionReason : undefined);
    } else {
      setEmailPreview('');
      setShowPreview(false);
    }
  }, [confirmAction, fetchEmailPreview]);

  // Debounced update for rejection reason preview
  useEffect(() => {
    if (confirmAction?.type === 'reject') {
      // Debounce the API call
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        fetchEmailPreview('reject', confirmAction.row.userEmail, rejectionReason);
      }, 500);
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [rejectionReason, confirmAction, fetchEmailPreview]);

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setSelectedIds(new Set()); // Clear selection when changing filter
    fetchRequests(1, newStatus);
  };

  const handleApprove = async (rowId) => {
    try {
      await approveRetryRequest(rowId);
      setConfirmAction(null);
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
      fetchRequests(pagination.page, statusFilter);
    } catch (err) {
      console.error('Failed to approve retry request:', err);
    }
  };

  const handleReject = async (rowId, reason) => {
    try {
      await rejectRetryRequest(rowId, reason);
      setConfirmAction(null);
      setRejectionReason('');
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
      fetchRequests(pagination.page, statusFilter);
    } catch (err) {
      console.error('Failed to reject retry request:', err);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setBulkApproving(true);
    try {
      await bulkApproveRetryRequests(Array.from(selectedIds));
      setSelectedIds(new Set());
      fetchRequests(pagination.page, statusFilter);
    } catch (err) {
      console.error('Failed to bulk approve:', err);
    } finally {
      setBulkApproving(false);
    }
  };

  const columns = [
    { key: 'userEmail', label: 'User Email' },
    {
      key: 'requestNumber',
      label: 'Request #',
      render: (val) => (
        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-600/20 text-blue-400">
          #{val || 1}
        </span>
      ),
    },
    {
      key: 'questionsSolved',
      label: 'Progress',
      render: (val) => (
        <span className="font-medium text-amber-400">
          {val || 0}/7 captured
        </span>
      ),
    },
    {
      key: 'requestedAt',
      label: 'Requested At (IST)',
      render: (val) => formatToIST(val),
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            val === 'PENDING'
              ? 'bg-yellow-600/20 text-yellow-400'
              : val === 'APPROVED' || val === 'CONSUMED'
              ? 'bg-green-600/20 text-green-400'
              : 'bg-red-600/20 text-red-400'
          }`}
        >
          {val || 'PENDING'}
        </span>
      ),
    },
  ];

  // Add processing columns for non-pending requests
  const columnsWithApprover = statusFilter !== 'PENDING' ? [
    ...columns,
    {
      key: 'approvedBy',
      label: 'Processed By',
      render: (val) => val || '-',
    },
    {
      key: 'approvedAt',
      label: statusFilter === 'REJECTED' ? 'Rejected At (IST)' : 'Approved At (IST)',
      render: (val) => (val ? formatToIST(val) : '-'),
    },
    // Consumed At only makes sense on the Approved tab (APPROVED + CONSUMED rows)
    ...(statusFilter === 'APPROVED' ? [{
      key: 'consumedAt',
      label: 'Consumed At (IST)',
      render: (val) => (val ? formatToIST(val) : '-'),
    }] : []),
  ] : columns;

  const actions = (row) => {
    // Only show actions for PENDING requests
    if (row.status !== 'PENDING') {
      return <span className="text-gray-500 text-sm">-</span>;
    }
    return (
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => setConfirmAction({ type: 'approve', row })}
          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
        >
          Approve
        </button>
        <button
          onClick={() => setConfirmAction({ type: 'reject', row })}
          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
        >
          Reject
        </button>
      </div>
    );
  };

  const statusTabs = [
    { value: 'PENDING', label: 'Pending', color: 'yellow' },
    { value: 'APPROVED', label: 'Approved', color: 'green' },
    { value: 'REJECTED', label: 'Rejected', color: 'red' },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Retry Requests</h1>
        <div className="flex gap-3 items-center">
          {statusFilter === 'PENDING' && selectedIds.size > 0 && (
            <span className="text-gray-400 text-sm">
              {selectedIds.size} selected
            </span>
          )}
          {statusFilter === 'PENDING' && (
            <button
              onClick={handleBulkApprove}
              disabled={selectedIds.size === 0 || bulkApproving}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {bulkApproving && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              )}
              Approve Selected
            </button>
          )}
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => handleStatusFilterChange(tab.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === tab.value
                ? tab.color === 'yellow'
                  ? 'bg-yellow-600 text-white'
                  : tab.color === 'green'
                  ? 'bg-green-600 text-white'
                  : 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columnsWithApprover}
        data={requests}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => fetchRequests(page, statusFilter)}
        actions={actions}
        emptyMessage={`No ${statusFilter.toLowerCase()} retry requests.`}
        selectable={statusFilter === 'PENDING'}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        idKey="rowId"
      />

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 flex max-h-[90vh] overflow-hidden transition-all duration-300">
            {/* Left panel - Form */}
            <div className="w-[400px] p-6 overflow-y-auto">
              <h2 className="text-xl font-bold text-white mb-4">
                {confirmAction.type === 'approve' ? 'Approve Request' : 'Reject Request'}
              </h2>
              <p className="text-gray-300 mb-2">
                User: <strong>{confirmAction.row.userEmail}</strong>
              </p>
              <p className="text-gray-300 mb-2">
                Progress: <strong className="text-amber-400">{confirmAction.row.questionsSolved || 0}/7</strong> dinosaurs captured
              </p>
              <p className="text-gray-300 mb-6">
                Request #: <strong>{confirmAction.row.requestNumber || 1}</strong>
              </p>
              {confirmAction.type === 'approve' && (
                <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3 mb-6">
                  <p className="text-yellow-400 text-sm">
                    Approving this request will:
                  </p>
                  <ul className="text-yellow-300/70 text-sm mt-2 list-disc list-inside">
                    <li>Delete all question mappings for this user</li>
                    <li>Reset the user's timer on next access</li>
                    <li>Allow them to start fresh with new questions</li>
                  </ul>
                  <p className="text-yellow-400 text-sm mt-3">
                    An email notification will be sent to the user.
                  </p>
                </div>
              )}
              {confirmAction.type === 'reject' && (
                <div className="mb-6">
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Rejection Reason (will be sent to user via email):
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter the reason for rejection..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                </div>
              )}

              {/* Toggle Preview Button */}
              <div className="mb-4">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPreview ? 'Hide Email Preview' : 'Show Email Preview'}
                </button>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setConfirmAction(null);
                    setRejectionReason('');
                    setShowPreview(false);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    confirmAction.type === 'approve'
                      ? handleApprove(confirmAction.row.rowId)
                      : handleReject(confirmAction.row.rowId, rejectionReason)
                  }
                  className={`px-4 py-2 text-white rounded ${
                    confirmAction.type === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {confirmAction.type === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>

            {/* Right panel - Email Preview */}
            {showPreview && (
              <div className="w-[420px] border-l border-gray-700 flex flex-col">
                <div className="px-4 py-3 bg-gray-900 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-300">Email Preview</h3>
                  {loadingPreview && (
                    <div className="w-4 h-4 border-2 border-gray-600 border-t-cyan-400 rounded-full animate-spin"></div>
                  )}
                </div>
                <div className="flex-1 overflow-hidden bg-gray-900 p-2">
                  {emailPreview ? (
                    <div className="w-full h-full relative">
                      <iframe
                        srcDoc={emailPreview}
                        title="Email Preview"
                        className="absolute inset-0 border-0 rounded"
                        style={{
                          width: '600px',
                          height: '800px',
                          transform: 'scale(0.65)',
                          transformOrigin: 'top left',
                        }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      Loading preview...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRetryRequests;
